<template>
    <div class="text-editor">
        <editor-menu-bar v-slot="{ commands, isActive }" :editor="editor">
            <div class="text-editor__menubar">
                <button
                    type="button"
                    class="text-editor__menubar--button"
                    :class="{ 'text-editor__menubar--button-active': isActive.bold() }"
                    @click="commands.bold"
                >
                    <common-icon name="bold" size="tiny"></common-icon>
                </button>

                <button
                    type="button"
                    class="text-editor__menubar--button"
                    :class="{ 'text-editor__menubar--button-active': isActive.italic() }"
                    @click="commands.italic"
                >
                    <common-icon name="italic" size="tiny"></common-icon>
                </button>

                <button
                    type="button"
                    class="text-editor__menubar--button"
                    :class="{ 'text-editor__menubar--button-active': isActive.underline() }"
                    @click="commands.underline"
                >
                    <common-icon name="underline" size="tiny-small"></common-icon>
                </button>

                <div class="text-editor__menubar--line"></div>

                <button
                    type="button"
                    class="text-editor__menubar--button"
                    :class="{ 'text-editor__menubar--button-active': isActive.ordered_list() }"
                    @click="commands.ordered_list"
                >
                    <common-icon name="olist" size="semi-small"></common-icon>
                </button>

                <button
                    type="button"
                    class="text-editor__menubar--button"
                    :class="{ 'text-editor__menubar--button-active': isActive.bullet_list() }"
                    @click="commands.bullet_list"
                >
                    <common-icon name="ulist" size="semi-small"></common-icon>
                </button>

                <div class="text-editor__menubar--line"></div>

                <button
                    v-if="showUploadFileIcon"
                    type="button"
                    class="text-editor__menubar--button"
                    @click="emitEvent({ name: 'attach-file' })"
                >
                    <common-icon
                        :hintText="attachmentHintText"
                        :hintHeader="attachmentHintHeader"
                        name="attach"
                        size="semi-small"
                    ></common-icon>
                </button>
            </div>
        </editor-menu-bar>

        <div class="editor-content-wrapper">
            <editor-content :class="editorContentClass" :editor="editor"></editor-content>
            <div v-if="overlayText && !$attrs.value" :class="overlayClass" @click="emitEvent({ name: 'attach-file' })">
                <common-icon name="cloud" size="ultra-large"></common-icon>
                <div class="editor-content-wrapper__overlay__text">{{ overlayText }}</div>
            </div>
        </div>

        <div v-if="uploadedFiles.length" :class="filesClass">
            <common-file-labels
                ref="fileLabels"
                :files="uploadedFiles"
                @show-all-files="showMoreFiles"
                @remove="emitEvent({ name: 'remove-file', payload: $event })"
            ></common-file-labels>
            <common-icon
                v-if="isFilesTabExpended"
                class="text-editor__files--close"
                name="close"
                size="tiny-small"
                @click="hideMoreFiles"
            ></common-icon>
        </div>
    </div>
</template>

<script>
    import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
    import {
        Placeholder,
        CodeBlock,
        OrderedList,
        BulletList,
        ListItem,
        Bold,
        Italic,
        Underline,
        History,
    } from 'tiptap-extensions'

    export default {
        components: {
            EditorContent,
            EditorMenuBar,
        },

        props: {
            placeholder: { type: String, default: undefined },
            prefill: { type: String, default: undefined },
            uploadedFiles: { type: Array, required: true },
            showUploadFileIcon: { type: Boolean, default: true },
            attachmentHintText: { type: String, default: '' },
            attachmentHintHeader: { type: String, default: '' },
            overlayText: { type: String, default: '' },
        },

        data() {
            return {
                isFilesTabExpended: false,
                isValueLoaded: false,
                editor: new Editor({
                    extensions: [
                        new CodeBlock(),
                        new OrderedList(),
                        new BulletList(),
                        new ListItem(),
                        new Bold(),
                        new Italic(),
                        new Underline(),
                        new History(),
                        new Placeholder({
                            emptyNodeClass: 'is-empty',
                            emptyNodeText: this.placeholder,
                            showOnlyWhenEditable: true,
                        }),
                    ],
                    onUpdate: ({ getHTML }) => {
                        this.emitEvent({ name: 'change', payload: this.getPayload(getHTML()) })
                    },
                }),
            }
        },

        computed: {
            editorContentClass() {
                return {
                    'text-editor__content': true,
                    'text-editor__content--small': this.uploadedFiles.length,
                }
            },

            filesClass() {
                return {
                    'text-editor__files': true,
                    'text-editor__files--expanded': this.isFilesTabExpended,
                }
            },

            overlayClass() {
                return {
                    'editor-content-wrapper__overlay': true,
                    'editor-content-wrapper__overlay--opaque': !!this.getPayload(this.editor.getHTML()),
                }
            },
        },

        methods: {
            showMoreFiles() {
                this.isFilesTabExpended = true
            },

            hideMoreFiles() {
                this.isFilesTabExpended = false
                this.$refs.fileLabels.hideFiles()
            },

            getPayload(html) {
                return html === '<p></p>' ? null : html
            },
        },

        watch: {
            '$attrs.value'() {
                const val = this.$attrs.value
                if (!val) {
                    this.editor.setContent('')
                }
                if (!this.isValueLoaded && val && !!val.length) {
                    this.editor.setContent(val)
                    this.isValueLoaded = true
                }
            },
        },

        beforeDestroy() {
            this.editor.destroy()
        },
    }
</script>
